import { useQuery, useQueryClient, UseQueryResult } from '@tanstack/react-query';
import { useMemo } from 'react';
import serviceToTQInterface from 'services/tq-adapter/serviceToTQInterface';
import { OneServiceNoParams, OneServiceWithParams } from 'types/services/services';

/**
 * Provide the ability to use a service without params.
 * @param service service without params to use
 * @returns service without params usage interface
 */
function useService<Data>(service: OneServiceNoParams<Data>): UseQueryResult<Data>;
/**
 * Provide the ability to use a service with params.
 * @param service service with params to use
 * @param params params of the service to use
 * @returns service with params usage interface
 */
function useService<Data, Params extends object>(service: OneServiceWithParams<Data, Params>, params: Params): UseQueryResult<Data>;
/**
 * Provide the ability to use a service.
 * @param service service to use
 * @param params params of the service to use
 * @returns service usage interface
 */
function useService<Data, Params extends object>(
  service: Params extends object ? OneServiceWithParams<Data, Params> : OneServiceNoParams<Data>,
  params?: Params,
) {
  const queryClient = useQueryClient();

  const { queryKey, ...options } = useMemo(
    () => serviceToTQInterface(queryClient, service, params),

    [params, queryClient, service],
  );

  return useQuery({ queryKey, ...options });
}

export default useService;
